import View from './view';
import ErrorPopup from '../error';
import Log from '../../util/log';
import WafCaptchaPopup from '../waf-captcha';

export default class Presenter {
  constructor(model, config, removeLoader) {
    this.model = model;
    this.config = config;
    this.removeLoader = removeLoader;
    this.hashSetup = false;
  }

  onAttach() {
    if (this.config && !this.hashSetup) {
      this.setup();
      this.hashSetup = true;
    }

    this.supportedLocales = this.config.supported_locales;
    this.defaultLanguage = this.config.default_language;

    const baseLanguage = navigator.language.split('-')[0];

    let selectedLanguage = 'en';

    // Use the browser's language, otherwise use the base language (e.g. 'en' for 'en-GB'), otherwise fallback onto the config's default language, otherwise 'en'.
    if (this.supportedLocales?.includes(navigator.language)) {
      selectedLanguage = navigator.language;
    } else if (this.supportedLocales?.includes(baseLanguage)) {
      selectedLanguage = baseLanguage;
    } else {
      let defaultLocale = 'en';
      if (typeof this.defaultLanguage === 'string') {
        defaultLocale = this.defaultLanguage;
      } else if (Array.isArray(this.defaultLanguage) && this.defaultLanguage.length > 0) {
        [defaultLocale] = this.defaultLanguage;
      }
      selectedLanguage = defaultLocale;
    }

    this.model.language = selectedLanguage;
    this.model.languagePolicies = this.config.claim.consent.policies[selectedLanguage];
  }

  update(hash, config) {
    this.model.hash = hash;
    this.model.config = config;
    this.config = config;
    if (this.config && !this.hashSetup) {
      this.setup();
      this.hashSetup = true;
    }
  }

  onRegister(notificationType) {
    this.model.values = {
      notificationType,
      index: View.Page.link,
    };
  }

  onContinueClick() {
    this.model.showMobileOnly = false;
  }

  setup() {
    const {
      app_id: appId,
      vlabs_api_url: vlabsApiUrl,
      vlabs_aws_waf_threat_detection_url: awsWafThreatDetectionUrl,
      vlabs_aws_waf_captcha_url: awsWafCaptchaUrl,
      vlabs_aws_waf_captcha_api: awsWafCaptchaApi,
      aws_waf_threat_detection_url: awsWafThreatDetectionUrlBackCompat,
      environment,
      version,
      key,
      campaign,
      domain,
      ga_tracking_id: ga,
      facebook_app_id: facebook,
      google_app_id: google,
      outside_app_id: outside,
      session_bypass: sessionBypass,
      signup_bypass: signupBypass,
      claim,
      pixel,
      error: errorConfig,
      platform_api_url: platformApiUrl,
      request_applet: requestAppletConfig,
    } = this.config;

    const {
      tradr_visit_pixel: tradrVisitPixel,
      tradr_visit_advertiser: tradrVisitAdvertiser,
      tradr_signup_pixel: tradrSignupPixel,
      tradr_signup_advertiser: tradrSignupAdvertiser,
      pixel_performance_visit: pixelPerformanceVisit,
      pixel_performance_signup: pixelPerformanceSignup,
    } = pixel;

    const { hostname } = window.location;
    if (domain && hostname !== domain) {
      const url = new URL(
        `https://${domain}${window.location.pathname}${window.location.hash}${window.location.search}`
      );
      window.location.replace(url);
      return;
    }

    if (requestAppletConfig) {
      const subDomain = window.location.hostname.split('.')[0];
      requestAppletConfig.url = `https://${subDomain}.ra.smartmedialabs.io`;
      requestAppletConfig.onready = (error) => {
        if (error) {
          ErrorPopup.show({ code: 'request_applet_init_error' }, () => {
            window.location.reload();
          });
        }
      };
    }

    const { delay: errorDelay, redirect_on_load: redirectOnLoadError } = errorConfig;

    if (claim?.expired) {
      this.model.index = View.Page.expired;
      this.removeLoader?.();
      return;
    }
    VlabsUser.init({
      key,
      config: {
        app_id: appId,
        vlabs_api_url: vlabsApiUrl,
        request_applet: requestAppletConfig?.enabled ? requestAppletConfig : undefined,
        platform_api_url: platformApiUrl,
        aws_waf_threat_detection_url:
          awsWafThreatDetectionUrl ?? awsWafThreatDetectionUrlBackCompat,
        aws_waf_captcha:
          (awsWafCaptchaUrl &&
            awsWafCaptchaApi && {
              url: awsWafCaptchaUrl,
              api: awsWafCaptchaApi,
              onrequest: () => WafCaptchaPopup.show(awsWafCaptchaApi),
            }) ||
          undefined,
        environment,
        version,
        campaign,
        ga,
        facebook_app: facebook,
        google_app: google,
        outside_app: outside,
        session_bypass: sessionBypass,
        signup_bypass: signupBypass,
        pixel_gtm: pixel?.gtm,
        pixel_facebook: pixel?.facebook,
        pixel_twitter: pixel?.twitter,
        pixel_visit: pixel?.visit,
        pixel_signup: pixel?.signup,
        tradr_visit_pixel: tradrVisitPixel,
        tradr_visit_advertiser: tradrVisitAdvertiser,
        tradr_signup_pixel: tradrSignupPixel,
        tradr_signup_advertiser: tradrSignupAdvertiser,
        pixel_performance_visit: pixelPerformanceVisit,
        pixel_performance_signup: pixelPerformanceSignup,
      },
    });

    VlabsUser.load(
      () => {
        const { email, phone } = VlabsUser.query || {};
        this.model.initialToken = email || phone;
        this.model.index = View.Page.landing;
        this.removeLoader?.();
        this.removeLoader = null;
      },
      () => {
        this.model.index = View.Page.link;
        this.removeLoader?.();
        this.removeLoader = null;
      },
      (error) => {
        ErrorPopup.show(error, () => {}, redirectOnLoadError && errorDelay);
        this.removeLoader?.();
        this.removeLoader = null;
        Log.log(error);
      }
    );
  }
}
